import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Navigate, Link } from 'react-router-dom';
import API from '../../services/config/api';
import '../../styles/portal-auth.scss';
import PasswordEyeToggle from '../../Components/PasswordEyeToggle';

function InternalAuditLogin() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const inviteToken = searchParams.get('token');

    const [form, setForm] = useState({ email: '', password: '' });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        const portalToken = localStorage.getItem('audit_portal_token');
        const mainSession = sessionStorage.getItem('access_token');
        if (portalToken && mainSession) {
            navigate('/audit-portal');
        }
    }, [navigate]);

    if (inviteToken) {
        return <Navigate to={`/audit-portal/signup?token=${inviteToken}`} replace />;
    }

    const handleLogin = async () => {
        if (!form.email || !form.password) { setError('Email and password are required.'); return; }
        setLoading(true); setError('');
        try {
            const axios = (await import('axios')).default;
            const authRes = await axios.post(
                API.AUDIT_PORTAL_LOGIN,
                { email: form.email, password: form.password },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'accept': 'application/json',
                        'Authorization': 'Basic YmFzZWFkbWluOmRlZmF1bHRANDU2',
                    },
                }
            );
            const authData = authRes?.data?.response_data?.data;
            if (!authData?.access_token) { setError('Invalid email or password.'); setLoading(false); return; }

            sessionStorage.setItem('access_token', authData.access_token);
            sessionStorage.setItem('refresh_token', authData.refresh_token);
            localStorage.setItem('user_profile', JSON.stringify(authData.user || {}));
            localStorage.setItem('audit_portal_token', authData.access_token);
            localStorage.setItem('audit_portal_refresh', authData.refresh_token);
            localStorage.setItem('audit_portal_user', JSON.stringify({
                email: authData.user?.email || authData.email || form.email,
                module: authData.user?.module || authData.module || 'audit',
                role: authData.user?.role || authData.role,
                first_name: authData.user?.first_name || authData.first_name,
                last_name: authData.user?.last_name || authData.last_name,
                organization_id: authData.user?.organization_id || authData.organization_id,
            }));
            navigate('/audit-portal');
        } catch (e) {
            const errMsg = e?.response?.data?.response_data?.message || e?.response?.data?.error?.message || '';
            setError(errMsg || 'Invalid email or password.');
        }
        setLoading(false);
    };

    const features = [
        'End-to-end audit lifecycle management',
        'Framework-based compliance controls',
        'Findings, CAPAs & evidence tracking',
        'Role-based collaboration & sign-offs',
    ];

    return (
        <div className="portal-auth-wrapper">
            {/* Left branding panel */}
            <div className="portal-left-panel">
                <div className="portal-left-content">
                    <p className="portal-title-main">Internal Audit</p>
                    <p className="portal-title-accent">Portal</p>
                    <p className="portal-tagline">
                        Streamlined audit management, compliance tracking, and real-time reporting — all in one place.
                    </p>
                    <div className="portal-features">
                        {features.map((text, i) => (
                            <div key={i} className="portal-feature-item">
                                <span className="portal-check">✓</span>
                                <span>{text}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Right form panel */}
            <div className="portal-form-panel">
                <div className="portal-form-wrapper">
                    <div className="portal-logo-wrap">
                        <img src="/assets/small_logo.svg" alt="NMT" width={300} height={90} onError={e => { e.target.style.display = 'none'; }} />
                    </div>

                    <div className="portal-card">
                        <h1 className="portal-card-title">Login</h1>
                        <p className="portal-card-subtitle">
                            Enter your company email below to login to your account.
                        </p>

                        {error && <div className="portal-alert portal-alert--error">{error}</div>}

                        <div className="portal-form-group">
                            <label htmlFor="ap-email">Work Email</label>
                            <input
                                id="ap-email"
                                type="email"
                                className="portal-input"
                                value={form.email}
                                onChange={e => setForm({ ...form, email: e.target.value })}
                                onKeyDown={e => e.key === 'Enter' && handleLogin()}
                                placeholder="user@company.com"
                                autoFocus
                            />
                        </div>

                        <div className="portal-form-group">
                            <label htmlFor="ap-password">Password</label>
                            <input
                                id="ap-password"
                                type={showPassword ? 'text' : 'password'}
                                className="portal-input portal-input--with-icon"
                                value={form.password}
                                onChange={e => setForm({ ...form, password: e.target.value })}
                                onKeyDown={e => e.key === 'Enter' && handleLogin()}
                                placeholder="••••••••••"
                            />
                            <PasswordEyeToggle visible={showPassword} onClick={() => setShowPassword(v => !v)} className="portal-eye-btn" />
                        </div>

                        <div className="portal-forgot-row">
                            <Link to="/audit-portal/forgot-password">Forgot your password?</Link>
                        </div>

                        <button className="portal-submit-btn" onClick={handleLogin} disabled={loading}>
                            {loading ? 'Signing in...' : 'Login'}
                        </button>

                        <div className="portal-card-footer">
                            NMT Security &nbsp;·&nbsp; Internal Audit Portal
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default InternalAuditLogin;
